/* eslint-disable react-hooks/exhaustive-deps */
/**
 * LM Mobile · Trip Assign + Trip Fill (Phase 15.10)
 * ------------------------------------------------------
 * One-screen mobile workflow for Logistics Managers in the field:
 *   1. Browse the fleet as cards with current driver + status chips.
 *   2. Tap a truck → bottom sheet with quick actions:
 *        • Assign Driver  (sets primary_driver + current_driver in one tap)
 *        • New Trip       (date, location, driver auto-from-truck, expense
 *                          auto-from-route-rate, optional settle now)
 *        • Update Status  (party / location / status chips)
 *   3. Designed mobile-first (cards, bottom sheets, large tap targets).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Truck,
  UserPlus,
  Plus,
  Pencil,
  Search,
  RefreshCw,
  X,
  Check,
  MapPin,
  IndianRupee,
  ChevronRight,
} from "lucide-react";

const STATUS_OPTIONS = [
  "on loading",
  "loaded",
  "on the way",
  "delivered",
  "on unloading",
  "return",
  "idle",
  "in workshop",
  "breakdown",
];

const STATUS_TONE = {
  "on loading": "bg-amber-100 text-amber-800",
  "loaded": "bg-sky-100 text-sky-800",
  "on the way": "bg-blue-100 text-blue-800",
  "delivered": "bg-emerald-100 text-emerald-800",
  "on unloading": "bg-amber-100 text-amber-800",
  "return": "bg-violet-100 text-violet-800",
  "idle": "bg-slate-100 text-slate-700",
  "in workshop": "bg-red-100 text-red-800",
  "breakdown": "bg-red-200 text-red-900",
};

function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmt(n) { return "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN"); }

export default function LMTripMobile() {
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);  // truck card opened

  const load = async () => {
    setLoading(true);
    try {
      const [t, dr, lo] = await Promise.all([
        api.get("/logistics/fleet-status?active_only=true"),
        api.get("/logistics/fleet-status/eligible-drivers"),
        api.get("/logistics/locations"),
      ]);
      setTrucks(t.data || []);
      setDrivers(dr.data || []);
      setLocations((lo.data || []).filter((l) => l.is_active !== false));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = trucks;
    if (q) {
      arr = arr.filter((r) =>
        (r.truck_no || "").toLowerCase().includes(q) ||
        (r.current_driver_name_resolved || "").toLowerCase().includes(q) ||
        (r.current_party || "").toLowerCase().includes(q) ||
        (r.current_location || "").toLowerCase().includes(q),
      );
    }
    if (filter === "assigned") arr = arr.filter((r) => r.current_driver_id);
    else if (filter === "unassigned") arr = arr.filter((r) => !r.current_driver_id);
    else if (filter === "moving") arr = arr.filter(
      (r) => ["on the way", "loaded", "on loading", "on unloading", "return"]
        .includes((r.current_status || "").toLowerCase()),
    );
    else if (filter === "idle") arr = arr.filter(
      (r) => !r.current_status || ["idle", "in workshop", "breakdown"]
        .includes((r.current_status || "").toLowerCase()),
    );
    return arr;
  }, [trucks, search, filter]);

  const counts = useMemo(() => ({
    all: trucks.length,
    assigned: trucks.filter((r) => r.current_driver_id).length,
    unassigned: trucks.filter((r) => !r.current_driver_id).length,
    moving: trucks.filter((r) => ["on the way", "loaded", "on loading",
      "on unloading", "return"].includes((r.current_status || "").toLowerCase())).length,
    idle: trucks.filter((r) => !r.current_status || ["idle", "in workshop",
      "breakdown"].includes((r.current_status || "").toLowerCase())).length,
  }), [trucks]);

  return (
    <div data-testid="lm-trip-mobile" className="space-y-3 pb-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            LM · Mobile Console
          </div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Trip Assign &amp; Fill
          </h1>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Tap a truck to assign driver, fill a trip, or update status.
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg ring-1 ring-slate-200 bg-white p-2"
          data-testid="lm-mobile-refresh"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search truck / driver / party / location…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 bg-white"
          data-testid="lm-mobile-search"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {[
          { k: "all", label: "All" },
          { k: "assigned", label: "Assigned" },
          { k: "unassigned", label: "Unassigned" },
          { k: "moving", label: "On Trip" },
          { k: "idle", label: "Idle/WS" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`lm-mobile-filter-${f.k}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-[11px] font-semibold ring-1 transition ${
              filter === f.k
                ? "bg-slate-900 text-white ring-slate-900"
                : "bg-white text-slate-700 ring-slate-200"
            }`}
          >
            {f.label} <span className="opacity-70">· {counts[f.k]}</span>
          </button>
        ))}
      </div>

      {/* Truck cards */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-10 bg-white rounded-xl ring-1 ring-slate-200">
          No trucks match.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <TruckCard key={t.id} truck={t} onTap={() => setActive(t)} />
          ))}
        </div>
      )}

      {/* Action sheet */}
      {active && (
        <ActionSheet
          truck={active}
          drivers={drivers}
          locations={locations}
          onClose={() => setActive(null)}
          onChanged={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

function TruckCard({ truck, onTap }) {
  const status = (truck.current_status || "").toLowerCase();
  const tone = STATUS_TONE[status] || "bg-slate-100 text-slate-600";
  return (
    <button
      onClick={onTap}
      data-testid={`lm-mobile-card-${truck.id}`}
      className="w-full text-left bg-white rounded-xl ring-1 ring-slate-200 p-3 hover:ring-slate-300 transition flex items-center gap-3"
    >
      <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
        <Truck className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-sm text-slate-900">{truck.truck_no}</div>
          {truck.current_status && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${tone}`}>
              {truck.current_status}
            </span>
          )}
          {!truck.current_driver_id && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-red-50 text-red-700 ring-1 ring-red-200">
              Unassigned
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5 truncate">
          <span className="font-semibold text-slate-700">
            {truck.current_driver_name_resolved || "— No driver —"}
          </span>
          {truck.current_party && <span className="text-slate-400"> · {truck.current_party}</span>}
        </div>
        {truck.current_location && (
          <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" />{truck.current_location}
          </div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}

function ActionSheet({ truck, drivers, locations, onClose, onChanged }) {
  const [mode, setMode] = useState("menu"); // menu | assign | trip | status

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="lm-mobile-sheet"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              {mode === "menu" ? "Truck Actions" :
                mode === "assign" ? "Assign Driver" :
                mode === "trip" ? "New Trip" : "Update Status"}
            </div>
            <h2 className="text-lg font-bold">{truck.truck_no}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 p-1" data-testid="lm-mobile-sheet-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {mode === "menu" && (
          <div className="space-y-2">
            <ActionBtn
              icon={UserPlus}
              label="Assign Driver"
              hint={truck.current_driver_name_resolved
                ? `Current: ${truck.current_driver_name_resolved}`
                : "No driver assigned"}
              onClick={() => setMode("assign")}
              testid="lm-mobile-assign-btn"
              tone="indigo"
            />
            <ActionBtn
              icon={Plus}
              label="Fill New Trip"
              hint="Log today's trip with auto rate fill"
              onClick={() => setMode("trip")}
              testid="lm-mobile-trip-btn"
              tone="emerald"
              disabled={!truck.current_driver_id}
              disabledHint={!truck.current_driver_id ? "Assign a driver first" : ""}
            />
            <ActionBtn
              icon={Pencil}
              label="Update Status"
              hint={truck.current_status || "Set party / location / status"}
              onClick={() => setMode("status")}
              testid="lm-mobile-status-btn"
              tone="sky"
            />
          </div>
        )}

        {mode === "assign" && (
          <AssignDriverForm
            truck={truck}
            drivers={drivers}
            onBack={() => setMode("menu")}
            onSaved={onChanged}
          />
        )}

        {mode === "trip" && (
          <NewTripForm
            truck={truck}
            drivers={drivers}
            locations={locations}
            onBack={() => setMode("menu")}
            onSaved={onChanged}
          />
        )}

        {mode === "status" && (
          <UpdateStatusForm
            truck={truck}
            drivers={drivers}
            onBack={() => setMode("menu")}
            onSaved={onChanged}
          />
        )}
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, hint, onClick, testid, tone = "slate", disabled, disabledHint }) {
  const tones = {
    indigo: "bg-indigo-50 ring-indigo-200 text-indigo-900",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    sky: "bg-sky-50 ring-sky-200 text-sky-900",
    slate: "bg-slate-50 ring-slate-200 text-slate-900",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center gap-3 p-3 rounded-xl ring-1 transition ${
        disabled ? "bg-slate-50 ring-slate-200 text-slate-400 cursor-not-allowed" : `${tones[tone]} hover:opacity-90`
      }`}
    >
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        disabled ? "bg-slate-200" : "bg-white"
      }`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 text-left">
        <div className="font-bold text-sm">{label}</div>
        <div className="text-[11px] opacity-80">{disabled ? disabledHint : hint}</div>
      </div>
      <ChevronRight className="h-4 w-4 opacity-60" />
    </button>
  );
}

/* -------------------------------------------------------------------- */
/* Assign Driver                                                          */
/* -------------------------------------------------------------------- */
function AssignDriverForm({ truck, drivers, onBack, onSaved }) {
  const [driverId, setDriverId] = useState(truck.current_driver_id || "");
  const [setPrimary, setSetPrimary] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return drivers;
    return drivers.filter((d) =>
      (d.name || "").toLowerCase().includes(ql) ||
      (d.employee_code || "").toLowerCase().includes(ql) ||
      (d.phone || "").includes(ql));
  }, [drivers, q]);

  const save = async () => {
    if (!driverId) return toast.error("Select a driver");
    const d = drivers.find((x) => x.id === driverId);
    setBusy(true);
    try {
      // 1) Always update current driver via fleet-status patch
      await api.patch(`/logistics/trucks/${truck.id}/fleet-status`, {
        current_driver_id: driverId,
        current_driver_name: d?.name || null,
      });
      // 2) Optionally set primary driver (1:1 mapping)
      if (setPrimary) {
        await api.patch(`/logistics/trucks/${truck.id}/primary-driver`, {
          primary_driver_id: driverId,
        });
      }
      toast.success(`Assigned ${d?.name} to ${truck.truck_no}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search driver by name / code / phone…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200"
          data-testid="lm-mobile-assign-driver-search"
        />
      </div>

      <div className="max-h-72 overflow-y-auto space-y-1 ring-1 ring-slate-100 rounded-lg p-1 bg-slate-50">
        {list.map((d) => (
          <button
            key={d.id}
            onClick={() => setDriverId(d.id)}
            data-testid={`lm-mobile-driver-${d.id}`}
            className={`w-full text-left p-2.5 rounded-lg flex items-center justify-between transition ${
              driverId === d.id
                ? "bg-indigo-600 text-white"
                : "bg-white text-slate-800 hover:bg-slate-100"
            }`}
          >
            <div>
              <div className="text-sm font-semibold">{d.name}</div>
              <div className={`text-[11px] ${driverId === d.id ? "text-indigo-100" : "text-slate-500"}`}>
                {d.employee_code || "—"} · {d.phone || "—"}
              </div>
            </div>
            {driverId === d.id && <Check className="h-4 w-4" />}
          </button>
        ))}
        {list.length === 0 && (
          <div className="text-center text-[11px] text-slate-500 py-6 px-3">
            {drivers.length === 0
              ? "No active drivers configured — contact HR to add drivers."
              : "No drivers match this search."}
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-700 px-1">
        <input
          type="checkbox"
          checked={setPrimary}
          onChange={(e) => setSetPrimary(e.target.checked)}
          data-testid="lm-mobile-set-primary"
          className="h-4 w-4"
        />
        Also set as <strong>primary driver</strong> (1:1, used by driver mobile app)
      </label>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          onClick={onBack}
          className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700"
        >
          Back
        </button>
        <button
          disabled={busy || !driverId}
          onClick={save}
          data-testid="lm-mobile-assign-save"
          className="rounded-xl bg-indigo-600 text-white py-3 text-sm font-bold disabled:bg-slate-300"
        >
          {busy ? "Saving…" : "Assign Driver"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* New Trip                                                               */
/* -------------------------------------------------------------------- */
function NewTripForm({ truck, drivers, locations, onBack, onSaved }) {
  const [date, setDate] = useState(todayIso());
  const [locationId, setLocationId] = useState("");
  const [driverId, setDriverId] = useState(truck.current_driver_id || "");
  const [expense, setExpense] = useState({
    rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0, other_remark: "",
  });
  const [amountToPay, setAmountToPay] = useState("");
  const [settleNow, setSettleNow] = useState(false);
  const [settleAmount, setSettleAmount] = useState("");
  const [routeRate, setRouteRate] = useState(null);
  const [locQ, setLocQ] = useState("");
  const [busy, setBusy] = useState(false);

  const driverName = useMemo(() => {
    const d = drivers.find((x) => x.id === driverId);
    return d?.name || truck.current_driver_name_resolved || "";
  }, [driverId, drivers, truck]);

  const filteredLocs = useMemo(() => {
    const q = locQ.trim().toLowerCase();
    if (!q) return locations.slice(0, 50);
    return locations.filter((l) =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.client_code || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [locations, locQ]);

  // Lookup route rate when truck + location chosen
  useEffect(() => {
    if (!locationId) {
      setRouteRate(null);
      return;
    }
    let cancelled = false;
    api.get("/logistics/route-rate/lookup", {
      params: { truck_no: truck.truck_no, location_id: locationId },
    }).then((r) => {
      if (cancelled) return;
      const cell = r.data?.rate || null;
      if (cell) {
        setRouteRate(cell);
        // Auto-fill only when expense is all-zero
        setExpense((prev) => {
          const allZero = ["rto", "cash", "tolls", "dala", "unloading", "other"]
            .every((k) => Number(prev[k] || 0) === 0);
          if (!allZero) return prev;
          return {
            ...prev,
            rto: cell.rto || 0,
            cash: cell.cash || 0,
            tolls: cell.tolls || 0,
            dala: cell.dala || 0,
            unloading: cell.unloading || 0,
            other: cell.other || 0,
          };
        });
        if (cell.amount_to_pay != null && amountToPay === "") {
          setAmountToPay(String(cell.amount_to_pay));
        }
      } else {
        setRouteRate(null);
      }
    }).catch(() => setRouteRate(null));
    return () => { cancelled = true; };
  }, [locationId, truck.truck_no]);

  const actualTotal = useMemo(() => (
    Number(expense.rto || 0) + Number(expense.cash || 0) +
    Number(expense.tolls || 0) + Number(expense.dala || 0) +
    Number(expense.unloading || 0) + Number(expense.other || 0)
  ), [expense]);

  const expectedRoute = routeRate?.expected_amount ?? null;
  const variance = expectedRoute != null ? actualTotal - expectedRoute : null;

  const save = async () => {
    if (!locationId) return toast.error("Pick a destination location");
    if (!driverId) return toast.error("Driver missing");
    setBusy(true);
    try {
      const payload = {
        date,
        truck_no: truck.truck_no,
        location_id: locationId,
        driver_id: driverId,
        expense,
        amount_to_pay: amountToPay === "" ? null : Number(amountToPay),
      };
      const { data: trip } = await api.post("/logistics/trips", payload);
      if (settleNow && Number(settleAmount) > 0) {
        await api.post(`/logistics/trips/${trip.id}/settle`, {
          amount_paid: Number(settleAmount), settled: true,
        });
      }
      toast.success("Trip logged");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Date + Driver banner */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Date</div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
            data-testid="lm-mobile-trip-date"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Driver</div>
          <select
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
            data-testid="lm-mobile-trip-driver"
          >
            <option value="">Select…</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {driverName && (
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">{driverName}</div>
          )}
        </div>
      </div>

      {/* Location picker */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
          Destination
        </div>
        <div className="relative mb-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={locQ}
            onChange={(e) => setLocQ(e.target.value)}
            placeholder="Search location…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200"
            data-testid="lm-mobile-loc-search"
          />
        </div>
        <div className="max-h-44 overflow-y-auto space-y-0.5 ring-1 ring-slate-100 rounded-lg p-1 bg-slate-50">
          {filteredLocs.map((l) => (
            <button
              key={l.id}
              onClick={() => setLocationId(l.id)}
              data-testid={`lm-mobile-loc-${l.id}`}
              className={`w-full text-left text-xs p-2 rounded-md flex items-center justify-between ${
                locationId === l.id
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-800 hover:bg-slate-100"
              }`}
            >
              <span className="truncate">
                <span className="font-semibold">{l.name}</span>
                {l.client_code && (
                  <span className={`text-[10px] ml-1 ${locationId === l.id ? "text-emerald-100" : "text-slate-400"}`}>
                    {l.client_code}
                  </span>
                )}
              </span>
              {locationId === l.id && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
            </button>
          ))}
          {filteredLocs.length === 0 && (
            <div className="text-center text-[11px] text-slate-400 py-3">No locations match.</div>
          )}
        </div>
      </div>

      {/* Route rate hint */}
      {routeRate && (
        <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded-lg p-2 text-[11px] text-emerald-800">
          Auto-filled from route rate · Expected:
          <strong className="ml-1">{fmt(routeRate.expected_amount)}</strong>
          {routeRate.amount_to_pay != null && (
            <span> · To pay: <strong>{fmt(routeRate.amount_to_pay)}</strong></span>
          )}
        </div>
      )}

      {/* Expense breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
          Expense breakdown
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            ["rto", "RTO"], ["cash", "Cash"], ["tolls", "Tolls"],
            ["dala", "Dala"], ["unloading", "Unloading"], ["other", "Other"],
          ].map(([k, label]) => (
            <div key={k}>
              <div className="text-[9px] uppercase text-slate-500">{label}</div>
              <input
                type="number"
                value={expense[k]}
                onChange={(e) => setExpense({ ...expense, [k]: e.target.value })}
                className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-1.5 text-sm"
                data-testid={`lm-mobile-trip-exp-${k}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-[11px] mt-1 px-1">
          <span className="text-slate-500">Actual total</span>
          <span className="font-bold text-slate-900 flex items-center">
            <IndianRupee className="h-3 w-3" />
            {Math.round(actualTotal).toLocaleString("en-IN")}
          </span>
        </div>
        {variance != null && Math.abs(variance) > 0 && (
          <div className={`text-[11px] mt-0.5 px-1 ${
            variance > 0 ? "text-red-600" : "text-emerald-600"
          }`}>
            {variance > 0 ? "Over by " : "Under by "}{fmt(Math.abs(variance))}
          </div>
        )}
      </div>

      {/* Amount to pay */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
          Amount to Pay (driver)
        </div>
        <input
          type="number"
          value={amountToPay}
          onChange={(e) => setAmountToPay(e.target.value)}
          placeholder="Default = route rate"
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-trip-pay"
        />
      </div>

      {/* Settle now */}
      <label className="flex items-center gap-2 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={settleNow}
          onChange={(e) => setSettleNow(e.target.checked)}
          data-testid="lm-mobile-trip-settle-toggle"
          className="h-4 w-4"
        />
        Settle now (cash given to driver)
      </label>
      {settleNow && (
        <input
          type="number"
          value={settleAmount}
          onChange={(e) => setSettleAmount(e.target.value)}
          placeholder="Amount paid now"
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-trip-settle-amount"
        />
      )}

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          onClick={onBack}
          className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700"
        >
          Back
        </button>
        <button
          disabled={busy || !locationId || !driverId}
          onClick={save}
          data-testid="lm-mobile-trip-save"
          className="rounded-xl bg-emerald-600 text-white py-3 text-sm font-bold disabled:bg-slate-300"
        >
          {busy ? "Saving…" : "Log Trip"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Update Status                                                          */
/* -------------------------------------------------------------------- */
function UpdateStatusForm({ truck, drivers, onBack, onSaved }) {
  const [driverId, setDriverId] = useState(truck.current_driver_id || "");
  const [party, setParty] = useState(truck.current_party || "");
  const [location, setLocation] = useState(truck.current_location || "");
  const [status, setStatus] = useState(truck.current_status || "");
  const [remarks, setRemarks] = useState(truck.remarks || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const d = drivers.find((x) => x.id === driverId);
      await api.patch(`/logistics/trucks/${truck.id}/fleet-status`, {
        current_driver_id: driverId || null,
        current_driver_name: driverId ? (d?.name || null) : null,
        current_party: party || null,
        current_location: location || null,
        current_status: status || null,
        remarks: remarks || null,
      });
      toast.success("Status updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Driver</div>
        <select
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-st-driver"
        >
          <option value="">— None —</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Party</div>
        <input
          value={party}
          onChange={(e) => setParty(e.target.value)}
          placeholder="e.g. Devgiri / moldmaan"
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-st-party"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Location</div>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. Sitapura / Sonipat"
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-st-location"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Status</div>
        <div className="grid grid-cols-3 gap-1.5">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`lm-mobile-st-opt-${s.replace(/\s+/g, "-")}`}
              className={`text-[11px] py-1.5 rounded-lg ring-1 ${
                status === s
                  ? "bg-slate-900 text-white ring-slate-900"
                  : "bg-white text-slate-700 ring-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Remarks</div>
        <input
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm"
          data-testid="lm-mobile-st-remarks"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          onClick={onBack}
          className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700"
        >
          Back
        </button>
        <button
          disabled={busy}
          onClick={save}
          data-testid="lm-mobile-st-save"
          className="rounded-xl bg-sky-600 text-white py-3 text-sm font-bold disabled:bg-slate-300"
        >
          {busy ? "Saving…" : "Update"}
        </button>
      </div>
    </div>
  );
}
